import React, { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { omit } from "lodash";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { FilterLabels, ActionOnKeyPress, CollapseSection } from "components";
import { filtersActions } from "store";
import { getFilters } from "store/filters/filters.reducer";
import { FilterColumn, FilterRow, FiltersType } from "types";

import FilterActions from "./filterActions";
import FiltersContext from "./filtersContext";
import { DefaultLayout, ExpandableLayout } from "./layout";
import useFiltersProvider from "./useFiltersProvider";

import styles from "./filter.module.scss";

export type FiltersProps = {
	form: string;
	loading?: boolean;
	activeFilterSet?: string;
	columns: FilterRow | FilterRow[] | Array<Array<FilterColumn>>;
	expandableRows?: FilterRow[];
	formatLabels?: any;
	formatTime?: string;
	initialValues: { [key: string]: any };
	hideLabels?: string[];
	strictResetEmptyFilterValues?: Array<string>;
	isRemoveActiveFilterSet?: boolean;
	filterSet?: { [key: string]: any } | Object;
	setActiveFilterSetId?: Function;
	setIsRemoveActiveFilterSet?: Function;
	showExtractButton?: boolean;
	excludedLabels?: string[];
};

const Filters = ({
	form,
	columns,
	expandableRows = [],
	strictResetEmptyFilterValues = [],
	loading,
	formatTime,
	initialValues = {},
	hideLabels,
	activeFilterSet,
	setActiveFilterSetId,
	setIsRemoveActiveFilterSet,
	isRemoveActiveFilterSet,
	showExtractButton = false,
	excludedLabels = [],
}: FiltersProps) => {
	const dispatch = useDispatch();
	const filters: FiltersType = useSelector(getFilters(form));
	const [isApplyDisabled, setIsApplyDisabled] = useState(false);

	const methods = useForm({
		reValidateMode: "onBlur",
		shouldUnregister: false,
	});

	const { reset, setValue, handleSubmit, getValues } = methods;

	const {
		allColumns,
		layoutContainerStyle,
		findColumn,
		isLabelForMultiValue,
		formatLabels,
	} = useFiltersProvider({ columns, expandableRows });

	// Reinitialize form state when switching tabs
	useEffect(() => {
		// Form values have different object shape from store
		// So return and do not init values
		const isFormEmpty = !Object.values(getValues())?.some(value => !!value);
		if (isFormEmpty) reset(filters);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (activeFilterSet) {
			reset({});
		}

		if (isRemoveActiveFilterSet) {
			reset(initialValues);
			dispatch(filtersActions.reset(form));
			setActiveFilterSetId?.("");
			setIsRemoveActiveFilterSet?.(false);
		}

		if (!activeFilterSet && !isRemoveActiveFilterSet) {
			reset(initialValues);
			dispatch(
				filtersActions.applyFilters({
					form,
					filters: initialValues,
				}),
			);
		}
	}, [
		activeFilterSet,
		dispatch,
		form,
		initialValues,
		isRemoveActiveFilterSet,
		reset,
		setActiveFilterSetId,
		setIsRemoveActiveFilterSet,
	]);

	const cleanFormValues = useCallback(() => {
		for (let clave in getValues()) {
			if (initialValues[clave]) {
				setValue(clave, initialValues[clave]);
			} else {
				setValue(clave, null);
			}
		}
		reset(initialValues);
	}, [setValue, initialValues, getValues, reset]);

	const handleRemoveFilter = useCallback(
		(filterKey, index) => {
			dispatch(
				filtersActions.removeKey(form, filterKey, allColumns, index),
			);

			const filter = allColumns?.find(
				column => column.name === filterKey,
			);
			if (filter && "onRemoveFilter" in filter) {
				filter.onRemoveFilter?.(index);
			}

			cleanFormValues();
		},
		[allColumns, dispatch, form, cleanFormValues],
	);

	const handleResetFilters = useCallback(() => {
		cleanFormValues();
		dispatch(filtersActions.reset(form, excludedLabels));
		setActiveFilterSetId?.("");
	}, [dispatch, form, cleanFormValues, setActiveFilterSetId, excludedLabels]);

	const applyFilters = useCallback(
		values => {
			if (!isApplyDisabled) {
				dispatch(
					filtersActions.applyFilters({
						form,
						filters: values,
						columns: allColumns,
						strictResetEmptyFilterValues,
					}),
				);
				cleanFormValues();
			}
		},
		[
			isApplyDisabled,
			dispatch,
			form,
			allColumns,
			cleanFormValues,
			strictResetEmptyFilterValues,
		],
	);

	const setIsExported = useCallback(
		() => dispatch(filtersActions.setIsExported(form, true)),
		[dispatch, form],
	);

	const showUpdateFilterLabels = filters =>
		hideLabels ? omit(filters, hideLabels) : filters;

	const onHandleExtractData = () => {
		const values = getValues();
		applyFilters(values);
		setIsExported();
	};

	const getLabels = (filters = {}) => {
		const labels = { ...filters };

		excludedLabels?.forEach(label => {
			Object.keys(filters)?.forEach(key => {
				if (key === label) {
					delete labels[key];
				}
			});
		});
		return showUpdateFilterLabels(labels);
	};

	return (
		<FormProvider {...methods}>
			<FiltersContext.Provider
				value={{
					loading,
					isApplyDisabled,
					setIsApplyDisabled,
					isLabelForMultiValue,
					findColumn,
				}}
			>
				<form
					onSubmit={handleSubmit(applyFilters)}
					className={styles.filterForm}
				>
					<div className={styles.filterHeader}>
						<FilterLabels
							filters={getLabels(filters)}
							formatLabels={formatLabels}
							formatTime={formatTime}
							onRemove={handleRemoveFilter}
							className={styles.filterLabels}
						/>
						<FilterActions
							onHandleExtractData={onHandleExtractData}
							onApply={handleSubmit(applyFilters)}
							onReset={handleResetFilters}
							showExtractButton={showExtractButton}
						/>
					</div>
					<ActionOnKeyPress
						{...layoutContainerStyle}
						keyName="Enter"
						preventDefault
						action={handleSubmit(applyFilters)}
						className={clsx(styles.filterFields)}
					>
						<DefaultLayout columns={columns} />
					</ActionOnKeyPress>
					{expandableRows?.length > 0 && (
						<ActionOnKeyPress
							keyName="Enter"
							preventDefault
							action={handleSubmit(applyFilters)}
						>
							<CollapseSection
								header="Additional Filters"
								className={styles.expandableFilter}
								classNameHeaderContent={styles.expandableHeader}
							>
								<ExpandableLayout rows={expandableRows} />
							</CollapseSection>
						</ActionOnKeyPress>
					)}
				</form>
			</FiltersContext.Provider>
		</FormProvider>
	);
};

export default Filters;
